import { ApiFormattedResponse } from "@models/api_formatted_response"
import { mutate, query } from "@utils/graphql"
import deleteAiHelpArticleMutation from "__generated__/queries/mutations/deleteAiHelpArticle"
import importAiHelpPdfMutation from "__generated__/queries/mutations/importAiHelpPdf"
import reindexAiHelpArticleMutation from "__generated__/queries/mutations/reindexAiHelpArticle"
import resolveAiHelpGapsMutation from "__generated__/queries/mutations/resolveAiHelpGaps"
import saveAiHelpArticleMutation from "__generated__/queries/mutations/saveAiHelpArticle"
import updateAiHelpArticleMutation from "__generated__/queries/mutations/updateAiHelpArticle"
import getAiHelpArticlesQuery from "__generated__/queries/queries/getAiHelpArticles"
import getAiHelpGapsQuery from "__generated__/queries/queries/getAiHelpGaps"
import searchAiHelpQuery from "__generated__/queries/queries/searchAiHelp"

export type AiHelpImage = {
  key: string
  url: string
  caption?: string | null
  description?: string | null
}

export type AiHelpSection = {
  key: string
  heading: string
  text: string
  images: AiHelpImage[]
}

export type AiHelpArticleStatus = "INDEXING" | "READY" | "FAILED" | "ARCHIVED"

export type AiHelpArticle = {
  _id: string
  slug: string
  title: string
  category?: string | null
  summary?: string | null
  related?: string | null
  author?: string | null
  sourceDate?: string | null
  source: string
  fileName?: string | null
  panelPath?: string | null
  permissions?: string[] | null
  language: string
  sections: AiHelpSection[]
  faqs: { question: string; answer: string }[]
  status: AiHelpArticleStatus
  chunkCount: number
  indexedAt?: string | null
  error?: string | null
  useCount?: number | null
  lastUsedAt?: string | null
  createdAt: string
  updatedAt: string
}

export type AiHelpArticleDraft = {
  _id?: string
  slug?: string
  title: string
  summary?: string
  category?: string
  panelPath?: string
  permissions?: string[]
  sections: { key?: string; heading: string; text: string }[]
  faqs: { question: string; answer: string }[]
}

export type AiHelpSectionHit = {
  articleId: string
  slug: string
  title: string
  panelPath?: string | null
  contentId: string
  heading: string
  text: string
  images: AiHelpImage[]
  score: number
}

export type AiHelpGap = {
  _id: string
  customer: string
  cPerson: string
  question: string
  resolvedAt?: string | null
  article?: string | null
  createdAt: string
}

type Envelope<T> = {
  success?: boolean | null
  code?: string | null
  message?: string | null
  data?: T | null
}

const unwrap = <T>(
  response: ApiFormattedResponse<Record<string, Envelope<T> | undefined>>,
  field: string
): ApiFormattedResponse<T> => {
  if (!response.isSuccess) {
    return { isSuccess: false, errorMessage: response.errorMessage }
  }
  const payload = response.data?.[field]
  if (!payload?.success || payload.data == null) {
    return {
      isSuccess: false,
      errorMessage: payload?.message || payload?.code || "Request failed"
    }
  }
  return { isSuccess: true, data: payload.data }
}

const read = async <T>(
  document: Parameters<typeof query>[0]["query"],
  field: string,
  variables?: Record<string, unknown>
) =>
  unwrap<T>(
    await query<Record<string, Envelope<T> | undefined>>({
      query: document,
      variables,
      fetchPolicy: "network-only"
    }),
    field
  )

const write = async <T>(
  document: Parameters<typeof mutate>[0]["mutation"],
  field: string,
  data: Record<string, unknown>
) =>
  unwrap<T>(
    await mutate<Record<string, Envelope<T> | undefined>>({
      mutation: document,
      variables: { data }
    }),
    field
  )

export const getAiHelpArticles = () =>
  read<AiHelpArticle[]>(getAiHelpArticlesQuery, "getAiHelpArticles")

export const searchAiHelp = (data: { query: string; slugs?: string[] }) =>
  read<AiHelpSectionHit[]>(searchAiHelpQuery, "searchAiHelp", { data })

export const getAiHelpGaps = (resolved = false) =>
  read<AiHelpGap[]>(getAiHelpGapsQuery, "getAiHelpGaps", {
    data: { resolved }
  })

export const resolveAiHelpGaps = (data: {
  ids: string[]
  articleId?: string
}) => write<number>(resolveAiHelpGapsMutation, "resolveAiHelpGaps", data)

export const saveAiHelpArticle = (data: AiHelpArticleDraft) =>
  write<AiHelpArticle>(saveAiHelpArticleMutation, "saveAiHelpArticle", data)

export const importAiHelpPdf = (data: {
  pdf: File
  panelPath?: string
  category?: string
}) => write<AiHelpArticle>(importAiHelpPdfMutation, "importAiHelpPdf", data)

export const updateAiHelpArticle = (data: {
  _id: string
  panelPath?: string
  category?: string
  permissions?: string[]
  archived?: boolean
}) =>
  write<AiHelpArticle>(updateAiHelpArticleMutation, "updateAiHelpArticle", data)

export const reindexAiHelpArticle = (_id: string) =>
  write<AiHelpArticle>(reindexAiHelpArticleMutation, "reindexAiHelpArticle", {
    _id
  })

export const deleteAiHelpArticle = (_id: string) =>
  write<boolean>(deleteAiHelpArticleMutation, "deleteAiHelpArticle", { _id })
